import Image from "next/image";

import styles from "@/styles/containers/cart-item.module.scss";

import { CatalogHelpers, Currency } from "@/tools";

import { Button } from "@/components";
import { Minus, NoPicture, Plus } from "@/components/Icons";

import { OrderStorage } from "@/storage/OrderStorage";
import { OMath } from "@/tools/OMath";
import { useLocale, useTranslations } from "next-intl";

const Item = ({ product, onEditProduct, onRemoveProduct, cartItemIndex }) => {
  const t = useTranslations();

  const locale = useLocale();

  const selectedSize = (product.sizes || []).find((s) => s.selected);

  let selectedOptions = [];
  if (product.type !== "pizza") {
    selectedOptions = (product.items || [])
      .map((q) => q.items.filter((o) => o.selected))
      .flat();
  }

  const selectedDough = (product.doughs || []).find((e) => e.selected);

  let selectedFlavors = [];
  if (product.type === "pizza") {
    selectedFlavors = (product.items || []).filter((e) => e.selected);
  }

  const selectedEdge = (product.edges || []).find((e) => e.selected);

  const selectedExtras = (product.extra_ingredients || []).filter(
    (e) => e.selected
  );

  const calculateItemPrice = (item) => {
    const props = ["items", "sizes", "extra_ingredients", "doughs", "edges"];

    let total =
      Math.round((item.price || 0) * 100) *
      (item.selected !== false && item.quantity > 0 ? 1 : 0);
    for (const key of props) {
      if (item[key] && item[key].length > 0) {
        for (const subItem of item[key]) {
          total += calculateItemPrice(subItem) * 100;
        }
      }
    }

    return +OMath.div(total, 100).toFixed(2);
  };

  const decreaseQuantity = () => {
    product.quantity = product.quantity - 1;
    OrderStorage.updateCartItem(cartItemIndex, product);
    if (product.quantity === 0)
      return onRemoveProduct && onRemoveProduct(product);
  };

  const increaseQuantity = () => {
    product.quantity = product.quantity + 1;
    OrderStorage.updateCartItem(cartItemIndex, product);
  };

  const getQuestionsOverview = () => {
    const getQuestion = (questionName, options) => {
      if (options.length === 0) return;

      return (
        <div className={styles.question}>
          <p>{questionName}</p>
          <span>{options.map((option) => option).join(", ")}</span>
        </div>
      );
    };

    const extractSelectOptionsText = (q) => {
      const selectedOptions = q.items.filter(
        (option) => option.selected === true
      );
      const options = selectedOptions.map(
        (option) => `${option.quantity}x ${option.name}`
      );
      if (selectedOptions?.length === 0) return [];

      return options;
    };

    return product.items.map((question) => {
      const selectedOptions = question.items.filter(
        (option) => option.selected === true
      );
      const optionTexts = extractSelectOptionsText(question);
      return (
        <>
          {getQuestion(question.name, optionTexts)}
          {selectedOptions.map((sOption) => {
            return sOption.items?.map((subQuestion) => {
              const subOptionTexts = extractSelectOptionsText(subQuestion);
              return getQuestion(subQuestion.name, subOptionTexts);
            });
          })}
        </>
      );
    });
  };

  const getDoughOverview = () => {
    return (
      <div className={styles.extra}>
        <div className="d-flex align-center flex-1">
          <span className={styles.name}>
            {CatalogHelpers.getTranslation({
              item: selectedDough,
              field: "name",
              locale,
            })}
          </span>
        </div>
      </div>
    );
  };

  const getFlavorsOverview = () => {
    if (selectedFlavors.length === 1) {
      return (
        <div
          className={styles.extra}
          key={`flavor-overview-${selectedFlavors[0].id}`}
        >
          <div className="d-flex align-center flex-1">
            <span className={styles.name}>{selectedFlavors[0].name}</span>
          </div>
        </div>
      );
    }

    const halves = selectedFlavors.reduce(
      (total, flavor) => total + flavor.quantity,
      0
    );
    return selectedFlavors.map((flavor) => (
      <div className={styles.extra} key={`flavor-overview-${flavor.id}`}>
        <div className="d-flex align-center flex-1">
          <span className={styles.quantity}>
            {flavor.quantity} / {halves} &nbsp;
          </span>
          <span className={styles.name}>{flavor.name}</span>
        </div>
      </div>
    ));
  };

  const getEdgeOverview = () => {
    if (!selectedEdge) {
      return <p>{t("noThanks")}</p>;
    }

    return (
      <div className={styles.extra}>
        <div className="d-flex align-center flex-1">
          <span className={styles.name}>{selectedEdge.name}</span>
        </div>
      </div>
    );
  };

  const productName = CatalogHelpers.getTranslation({
    item: product,
    field: "name",
    locale,
  });

  return (
    <>
      <div className={styles.item}>
        <div className={styles.picture}>
          {product.image ? (
            <Image
              src={`${process.env.NEXT_PUBLIC_CDN_ASSETS}/${product.image}`}
              alt={productName}
              fill
            />
          ) : (
            <NoPicture />
          )}
        </div>
        <div className={styles.details}>
          <div className="d-flex align-center">
            <p className={styles.qty}>{product.quantity}x</p>
            <p className={styles.itemName}>{productName}</p>
          </div>

          {selectedSize && (
            <div className={styles.size}>
              <p>{t("size")}:&nbsp;</p>
              <span className={styles.name}>
                {CatalogHelpers.getTranslation({
                  item: product,
                  field: "name",
                  locale,
                })}
              </span>
            </div>
          )}

          {selectedOptions.length > 0 && (
            <div className={styles.questions}>{getQuestionsOverview()}</div>
          )}

          {selectedDough && (
            <div className={styles.extras}>
              <p>{t("dough")}</p>
              {getDoughOverview()}
            </div>
          )}

          {selectedFlavors.length > 0 && (
            <div className={styles.extras}>
              <p>{t("flavors")}</p>
              {getFlavorsOverview()}
            </div>
          )}

          {selectedEdge && (
            <div className={styles.extras}>
              <p>{t("edge")}</p>
              {getEdgeOverview()}
            </div>
          )}

          {selectedExtras.length > 0 && (
            <div className={styles.extras}>
              <p>{t("extras")}</p>
              {(selectedExtras || []).map((item, index) => (
                <div className={styles.extra} key={index}>
                  <div className="d-flex align-center flex-1">
                    <span className={styles.quantity}>
                      {item.quantity}x&nbsp;
                    </span>
                    <span className={styles.name}>
                      {CatalogHelpers.getTranslation({
                        item,
                        field: "name",
                        locale,
                      })}
                    </span>
                  </div>
                  <span className={styles.extraPrice}>
                    {Currency.formatCurrency(
                      item.sizes[0]?.price || item.price || 0
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}

          {product.observation && (
            <p className={styles.observation}>
              {t("note")} {product.observation}
            </p>
          )}

          <div className="d-flex align-center mt-10">
            <Button
              onClick={() => onEditProduct && onEditProduct(product)}
              design="default-mini"
            >
              {t("edit")}
            </Button>
            <Button
              onClick={onRemoveProduct}
              design="secondary-mini-danger"
              className="ml-15"
            >
              {t("remove")}
            </Button>
          </div>

          <div className={styles.total}>
            <div className="d-flex align-center w-100 justify-between">
              <p className={styles.value}>
                {Currency.formatCurrency(
                  CatalogHelpers.calculateSelectedItemPrice(product)
                )}
              </p>

              <div className={styles.quantitySpinner}>
                <div
                  className={styles.minus}
                  onClick={() => decreaseQuantity(product)}
                >
                  <Minus />
                </div>

                <p className={styles.quantity}>{product.quantity}</p>

                <div
                  className={styles.plus}
                  onClick={() => increaseQuantity(product)}
                >
                  <Plus />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default function CartItem({ order, onEditProduct, onRemoveProduct }) {
  return (
    <div className={styles.items}>
      {(order.cart.catalog.items || []).map((item, index) => (
        <Item
          key={`cart-item-${Math.random()}`}
          product={item}
          onEditProduct={(product) => onEditProduct(product, index)}
          onRemoveProduct={(product) =>
            onRemoveProduct && onRemoveProduct(product, index)
          }
          cartItemIndex={index}
        />
      ))}
    </div>
  );
}
